import React, { useEffect, useState } from 'react';
import { getScores, setScore, subscribe, computeOverall, resetScores } from 'services/signal/fmSignalScoreService';
import { FM_DIMENSIONS as SIGNALS } from 'services/signal/signalRegistry';
import { downloadJSON, downloadCSV } from 'utils/download';
import ThumbsupIcon from 'assets/Thumbsup.png';
import ThumbsdownIcon from 'assets/Thumbsdown.png';

const BADGE_STYLES = {
  'go': { background: '#1b5e20', color: '#fff' },
  'no-go': { background: '#b71c1c', color: '#fff' },
  'cautious': { background: '#f57f17', color: '#222' },
  'default': { background: '#ececec', color: '#555' }
};

const ScorePill = ({ value }) => {
  if(!value) return <span style={{ ...BADGE_STYLES.default, padding: '2px 10px', borderRadius: 20, fontSize: 12 }}>Unscored</span>;
  const style = BADGE_STYLES[value];
  const icon = value === 'go' ? <><img src={ThumbsupIcon} alt="👍" style={{height: 12, width: 12, display: 'inline'}} /><img src={ThumbsupIcon} alt="👍" style={{height: 12, width: 12, display: 'inline'}} /></> : value === 'cautious' ? <img src={ThumbsupIcon} alt="👍" style={{height: 12, width: 12, display: 'inline'}} /> : <img src={ThumbsdownIcon} alt="👎" style={{height: 12, width: 12, display: 'inline'}} />;
  return <span style={{ ...style, padding: '2px 10px', borderRadius: 20, fontSize: 12 }}>{icon}</span>;
};

const OptionButton = ({ active, label, onClick, color }) => (
  <button onClick={onClick} style={{
    padding: '6px 12px',
    fontSize: 13,
    borderRadius: 6,
    border: active ? '2px solid #222' : '1px solid #ccc',
    background: active ? color : '#fff',
    color: active ? '#fff' : '#333',
    cursor: 'pointer',
    fontWeight: 600,
    boxShadow: active ? '0 0 0 2px rgba(0,0,0,0.15)' : 'none',
    transition: 'all .15s'
  }}>{label}</button>
);

const OverallBadge = ({ scores }) => {
  const overall = computeOverall(scores);
  let text = 'Unscored';
  let style = BADGE_STYLES.default;
  if(overall){
    text = overall === 'go' ? <span>Overall: <img src={ThumbsupIcon} alt="👍" style={{height: 14, width: 14, display: 'inline', verticalAlign: 'middle'}} /><img src={ThumbsupIcon} alt="👍" style={{height: 14, width: 14, display: 'inline', verticalAlign: 'middle'}} /></span> : overall === 'no-go' ? <span>Overall: <img src={ThumbsdownIcon} alt="👎" style={{height: 14, width: 14, display: 'inline', verticalAlign: 'middle'}} /></span> : <span>Overall: <img src={ThumbsupIcon} alt="👍" style={{height: 14, width: 14, display: 'inline', verticalAlign: 'middle'}} /></span>;
    style = BADGE_STYLES[overall];
  }
  return <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 30, fontSize: 14, fontWeight: 600, ...style }}>{text}</div>;
};

const FMSignal = () => {
  const [scores, setScores] = useState(() => getScores());

  useEffect(() => {
    const unsub = subscribe(setScores);
    return () => unsub();
  }, []);

  const handleSet = (id, value) => {
    setScore(id, value === scores[id] ? null : value);
  };

  return (
    <div style={{ padding: '2rem', maxWidth: 960, margin: '0 auto', fontFamily: 'Segoe UI, Arial, sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <h1 style={{ color: '#6a1b9a', fontWeight: 700, margin: 0 }}>FM Signal</h1>
        <OverallBadge scores={scores} />
      </div>
      <p style={{ fontSize: '1.05em', color: '#444', margin: '12px 0 28px' }}>
        Each signal will be scored with: <strong><img src={ThumbsupIcon} alt="👍" style={{height: 14, width: 14, display: 'inline', verticalAlign: 'middle'}} /><img src={ThumbsupIcon} alt="👍" style={{height: 14, width: 14, display: 'inline', verticalAlign: 'middle'}} /></strong>, <strong><img src={ThumbsupIcon} alt="👍" style={{height: 14, width: 14, display: 'inline', verticalAlign: 'middle'}} /></strong>, <strong><img src={ThumbsdownIcon} alt="👎" style={{height: 14, width: 14, display: 'inline', verticalAlign: 'middle'}} /></strong>. Choose the best fit for current FM assessment.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {SIGNALS.map(sig => {
          const value = scores[sig.id];
          return (
            <div key={sig.id} style={{ background: '#f7faff', border: '1px solid #dfe7f5', borderRadius: 10, padding: '14px 18px', boxShadow: '0 2px 4px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <h3 style={{ margin: 0, fontSize: 18, color: '#1a237e' }}>{sig.label}</h3>
                    <ScorePill value={value} />
                  </div>
                  <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>{sig.desc}</div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <OptionButton label={<><img src={ThumbsupIcon} alt="👍" style={{height: 14, width: 14, display: 'inline'}} /><img src={ThumbsupIcon} alt="👍" style={{height: 14, width: 14, display: 'inline'}} /></>} color="#2e7d32" active={value==='go'} onClick={()=> handleSet(sig.id,'go')} />
                  <OptionButton label={<img src={ThumbsupIcon} alt="👍" style={{height: 14, width: 14, display: 'inline'}} />} color="#f9a825" active={value==='cautious'} onClick={()=> handleSet(sig.id,'cautious')} />
                  <OptionButton label={<img src={ThumbsdownIcon} alt="👎" style={{height: 14, width: 14, display: 'inline'}} />} color="#c62828" active={value==='no-go'} onClick={()=> handleSet(sig.id,'no-go')} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 32, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <button onClick={()=> resetScores()} style={{ background:'#fff', border:'1px solid #bbb', padding:'8px 14px', borderRadius:6, cursor:'pointer', fontWeight:600 }}>Reset All</button>
        <button onClick={()=> alert('Scores saved locally. (Persistence via localStorage)')} style={{ background:'#1a237e', color:'#fff', border:'none', padding:'8px 16px', borderRadius:6, cursor:'pointer', fontWeight:600 }}>Save Snapshot</button>
        <button onClick={()=> downloadJSON({ scores, overall: computeOverall(scores), type:'FM' }, 'fm-signal-scores.json')} style={{ background:'#ffffff', color:'#333', border:'1px solid #bbb', padding:'8px 16px', borderRadius:6, cursor:'pointer', fontWeight:600 }}>Export JSON</button>
        <button onClick={()=> {
          const rows = [ ['signal','score'] , ...Object.entries(scores||{}) ];
          downloadCSV(rows, 'fm-signal-scores.csv');
        }} style={{ background:'#ffffff', color:'#333', border:'1px solid #bbb', padding:'8px 16px', borderRadius:6, cursor:'pointer', fontWeight:600 }}>Export CSV</button>
      </div>

          {/* Ratings Guide */}
      <div style={{ marginTop: 48, padding: '28px 24px', background:'#f7f2fb', border:'1px solid #e5dff0', borderRadius: 12 }}>
        <h4 style={{ margin: 0, marginBottom: 18, fontSize: 18, fontWeight: 700, color:'#4a148c' }}>Ratings Guide</h4>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <li style={{ display:'flex', alignItems:'center', gap:14 }}>
            <span aria-label="Very Good" title="Very Good" style={{display:'inline-flex', gap:4}}>
              <img src={ThumbsupIcon} alt="Very good" style={{height:20, width:20}} />
              <img src={ThumbsupIcon} alt="Very good" style={{height:20, width:20}} />
            </span>
            <span style={{ fontSize:14, color:'#333' }}>indicates very good, no complaints</span>
          </li>
          <li style={{ display:'flex', alignItems:'center', gap:14 }}>
            <span aria-label="Acceptable" title="Acceptable" style={{display:'inline-flex'}}>
              <img src={ThumbsupIcon} alt="Acceptable" style={{height:20, width:20}} />
            </span>
            <span style={{ fontSize:14, color:'#333' }}>indicates acceptable, with issues to monitor.</span>
          </li>
          <li style={{ display:'flex', alignItems:'center', gap:14 }}>
            <span aria-label="Not Good" title="Not Good" style={{display:'inline-flex'}}>
              <img src={ThumbsdownIcon} alt="Not good" style={{height:20, width:20}} />
            </span>
            <span style={{ fontSize:14, color:'#333' }}>indicates not good and requires immediate attention</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default FMSignal;
